import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ProviderPriceListItemOptionValueModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/providerPriceListItemOptionValue/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/providerPriceListItemOptionValue/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/providerPriceListItemOptionValue/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/providerPriceListItemOptionValue/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/providerPriceListItemOptionValue/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/providerPriceListItemOptionValue/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/providerPriceListItemOptionValue/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/providerPriceListItemOptionValue/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/providerPriceListItemOptionValue/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/providerPriceListItemOptionValue/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/providerPriceListItemOptionValue/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/providerPriceListItemOptionValue/DELETE_FAILURE'

export type ProviderPriceListItemOptionValueModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: ProviderPriceListItemOptionValueModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviderPriceListItemOptionValues(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().providerPriceListItemOptionValues.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/provider_price_list_item_option_values.json', options)
      .then(({ data }: { data: ProviderPriceListItemOptionValueModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadProviderPriceListItemOptionValue(providerPriceListItemOptionValueId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.providerPriceListItemOptionValues.loadedForKeys]
    const loadedIds = [...state.providerPriceListItemOptionValues.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (providerPriceListItemOptionValueId && !loadedIds.includes(providerPriceListItemOptionValueId)){
      loadedIds.push(providerPriceListItemOptionValueId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/provider_price_list_item_option_values/${providerPriceListItemOptionValueId}.json`, options)
      .then(({ data }: { data: ProviderPriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createProviderPriceListItemOptionValue(providerPriceListItemOptionValue: Partial<ProviderPriceListItemOptionValueModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_price_list_item_option_value: providerPriceListItemOptionValue,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/provider_price_list_item_option_values.json', options, config)
      .then(({ data }: { data: ProviderPriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateProviderPriceListItemOptionValue(
  providerPriceListItemOptionValue: UpdateParams<ProviderPriceListItemOptionValueModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_price_list_item_option_value: providerPriceListItemOptionValue,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_list_item_option_values/${providerPriceListItemOptionValue.id}.json`, options, config)
      .then(({ data }: { data: ProviderPriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateDefaultProviderPriceListItemOptionValue(
  providerPriceListItemOptionValue: UpdateParams<ProviderPriceListItemOptionValueModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(
      `/provider_price_list_item_option_values/${providerPriceListItemOptionValue.id}/update_default.json`,
      options,
      config,
    )
      .then(({ data }: { data: ProviderPriceListItemOptionValueModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))
        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateSortOrder(
  sortOrderParams: { sortOrder: number[] },
  options: EntityOptions = {},
) {
  const { sortOrder } = sortOrderParams || {}

  const config = {
    method: 'PUT',
    data: JSON.stringify({
      sort_order: sortOrder,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/provider_price_list_item_option_values/update_sort_order.json', options, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteProviderPriceListItemOptionValue(providerPriceListItemOptionValue: DeleteParams<ProviderPriceListItemOptionValueModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_price_list_item_option_values/${providerPriceListItemOptionValue.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ProviderPriceListItemOptionValueModuleState = initialState,
  action: Action = { type: '' },
): ProviderPriceListItemOptionValueModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
